import { fileService } from 'api/services/file'
import { pageService } from 'api/services/page'
import { portalService } from 'api/services/portal'
import { trainingService } from 'api/services/trainings'
import { webisodeService } from 'api/services/webisodes'
import { PageType } from 'api/types/page'
import { SettingsCode, SettingsType } from 'api/types/portal'
import { TrainingItem } from 'api/types/trainings'
import { Webisode } from 'api/types/webisode'
import { Wrapper } from 'components'
import {
  Button,
  Input,
  Select,
  SelectAction,
  SelectContent,
  SelectItem,
  SelectValue,
  Tabs,
  TabsContent,
  TabsItem,
  TabsList,
  toast,
  Upload,
  UploadBody,
  UploadText,
} from 'keep-react'
import { TrashSimple } from 'phosphor-react'
import { useEffect, useState } from 'react'

export interface SettingsObject {
  contact_bar_text: string
  phoneForContacts: string
  emailThatReceivesContacts: string
  emailThatReceivesSimulationRequests: string
  emailThatReceivesSinistersParticipations: string
  dashboardWebisode: string
  dashboardContacts: string
  dashboardTraining: string
  socialNetworkFacebook: string
  socialNetworkInstagram: string
  socialNetworkLinkedin: string
  socialNetworkYouTube: string
  pagesClaimsProcess: string
}

type SettingFile = {
  identifier: string
  type: string
  file: File
}

export const BackofficeSettings = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [webisodes, setWebisodes] = useState<Webisode[] | null>(null)
  const [pages, setPages] = useState<PageType[] | null>(null)
  const [trainings, setTrainings] = useState<TrainingItem[] | null>(null)
  const [settings, setSettings] = useState<SettingsObject>({
    contact_bar_text: '',
    phoneForContacts: '',
    dashboardWebisode: '',
    dashboardContacts: '',
    dashboardTraining: '',
    emailThatReceivesContacts: '',
    emailThatReceivesSimulationRequests: '',
    emailThatReceivesSinistersParticipations: '',
    socialNetworkFacebook: '',
    socialNetworkInstagram: '',
    socialNetworkLinkedin: '',
    socialNetworkYouTube: '',
    pagesClaimsProcess: '',
  })
  const [settingFiles, setSettingFiles] = useState<SettingFile[] | null>(null)

  const getTrainingList = async () => {
    try {
      const response = (await trainingService.getTrainingList({
        trainingStatus: '3',
      })) as TrainingItem[]
      setTrainings(response || [])
    } catch (error) {
      toast.error('Não foi possível carregar as formações.')
    }
  }

  const getWebisodeList = async () => {
    try {
      const response = await webisodeService.getWebisodesList()
      setWebisodes(response as Webisode[])
    } catch (error) {
      toast.error('Não foi possível carregar os webisodes.')
    }
  }

  const getPagesList = async () => {
    try {
      const response = await pageService.getPageList()
      setPages(response as PageType[])
    } catch (error) {
      toast.error('Não foi possível carregar os webisodes.')
    }
  }

  const getSettings = async () => {
    const response = (await portalService.getConfigList()) as SettingsType[]

    setSettings(prev => ({
      ...prev,
      dashboardWebisode:
        response.find(s => s.code.trim() === 'dashboardWebisode')?.value || '',
      dashboardContacts:
        response.find(s => s.code.trim() === 'dashboardContacts')?.value || '',
      dashboardTraining:
        response.find(s => s.code.trim() === 'dashboardTraining')?.value || '',
      contact_bar_text:
        response.find(s => s.code.trim() === 'contact_bar_text')?.value || '',
      phoneForContacts:
        response.find(s => s.code.trim() === 'phoneForContacts')?.value || '',
      emailThatReceivesContacts:
        response.find(s => s.code.trim() === 'emailThatReceivesContacts')
          ?.value || '',
      emailThatReceivesSimulationRequests:
        response.find(
          s => s.code.trim() === 'emailThatReceivesSimulationRequests',
        )?.value || '',
      emailThatReceivesSinistersParticipations:
        response.find(
          s => s.code.trim() === 'emailThatReceivesSinistersParticipations',
        )?.value || '',
      socialNetworkFacebook:
        response.find(s => s.code.trim() === 'socialNetworkFacebook')?.value ||
        '',
      socialNetworkInstagram:
        response.find(s => s.code.trim() === 'socialNetworkInstagram')?.value ||
        '',
      socialNetworkLinkedin:
        response.find(s => s.code.trim() === 'socialNetworkLinkedin')?.value ||
        '',
      socialNetworkYouTube:
        response.find(s => s.code.trim() === 'socialNetworkYouTube')?.value ||
        '',
      pagesClaimsProcess:
        response.find(s => s.code.trim() === 'pagesClaimsProcess')?.value || '',
    }))

    setIsLoading(false)
  }

  const saveSettings = async () => {
    try {
      const body = Object.entries(settings).map(([code, value]) => ({
        code: code as SettingsCode,
        value,
      }))
      await portalService.saveConfigList(body)
      toast.success('Configurações salvas com sucesso.')
    } catch (error) {
      toast.error('Não foi possível salvar as configurações.')
    }
  }

  const getFiles = async () => {
    try {
      const fichaNomeacao = await fileService.getFilesFromAzure({
        identifier: 'ficha_nomeacao',
        type: 'settings',
      })
      const autorizacaoDebitoDirecto = await fileService.getFilesFromAzure({
        identifier: 'autorizacao_debito_directo',
        type: 'settings',
      })
      const formularioDeSinistros = await fileService.getFilesFromAzure({
        identifier: 'formulario_de_sinistros',
        type: 'settings',
      })
      const comprovativoIbanInnovarisk = await fileService.getFilesFromAzure({
        identifier: 'comprovativo_de_iban_innovarisk',
        type: 'settings',
      })
      const declaracaoAusenciaSinistro = await fileService.getFilesFromAzure({
        identifier: 'declaracao_de_ausencia_de_sinistro',
        type: 'settings',
      })
      setSettingFiles([
        ...(fichaNomeacao.length > 0
          ? [
              {
                identifier: 'ficha_nomeacao',
                type: 'settings',
                file: fichaNomeacao[0].uri,
              },
            ]
          : []),
        ...(autorizacaoDebitoDirecto.length > 0
          ? [
              {
                identifier: 'autorizacao_debito_directo',
                type: 'settings',
                file: autorizacaoDebitoDirecto[0].uri,
              },
            ]
          : []),
        ...(formularioDeSinistros.length > 0
          ? [
              {
                identifier: 'formulario_de_sinistros',
                type: 'settings',
                file: formularioDeSinistros[0].uri,
              },
            ]
          : []),
        ...(comprovativoIbanInnovarisk.length > 0
          ? [
              {
                identifier: 'comprovativo_de_iban_innovarisk',
                type: 'settings',
                file: comprovativoIbanInnovarisk[0].uri,
              },
            ]
          : []),
        ...(declaracaoAusenciaSinistro.length > 0
          ? [
              {
                identifier: 'declaracao_de_ausencia_de_sinistro',
                type: 'settings',
                file: declaracaoAusenciaSinistro[0].uri,
              },
            ]
          : []),
      ])
    } catch (error) {
      // toast.error('Não foi possível carregar os ficheiros de configuração.')
    }
  }

  const onDrop = async (acceptedFiles: File, identifier: string) => {
    try {
      await fileService.addFilesToAzure(identifier, 'settings', acceptedFiles)
      setTimeout(() => {
        getFiles()
      }, 1000)
      toast.success('Ficheiro carregado com sucesso.')
    } catch (error) {
      toast.error('Não foi possível carregar o ficheiro.')
    }
  }

  const onDeleteFile = async (identifier: string) => {
    try {
      await fileService.deleteFileFromAzure(identifier, 'settings')
      setTimeout(() => {
        getFiles()
      }, 1000)
      toast.success('Ficheiro eliminado com sucesso.')
    } catch (error) {
      toast.error('Não foi possível eliminar o ficheiro.')
    }
  }

  useEffect(() => {
    getTrainingList()
    getWebisodeList()
    getPagesList()
    getSettings()
    getFiles()
  }, [])

  if (isLoading) return <> </>

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h1 className="pb-10 text-xxxl font-medium text-metal-900 flex items-center justify-between">
          Backoffice Settings
          <Button
            color="secondary"
            variant="softBg"
            onClick={() => saveSettings()}
          >
            Gravar configurações
          </Button>
        </h1>
        <div className="pt-6">
          <Tabs defaultValue="dashboard" className="w-full">
            <TabsList>
              <TabsItem
                className="data-[state=active]:text-white data-[state=active]:bg-primary-700"
                value="dashboard"
              >
                Dashboard
              </TabsItem>
              <TabsItem
                className="data-[state=active]:text-white data-[state=active]:bg-primary-700"
                value="redes-sociais"
              >
                Redes Sociais
              </TabsItem>
              <TabsItem
                className="data-[state=active]:text-white data-[state=active]:bg-primary-700"
                value="contatos"
              >
                Contatos
              </TabsItem>
              <TabsItem
                className="data-[state=active]:text-white data-[state=active]:bg-primary-700"
                value="paginas"
              >
                Páginas
              </TabsItem>
              <TabsItem
                className="data-[state=active]:text-white data-[state=active]:bg-primary-700"
                value="ficheiros"
              >
                Ficheiros
              </TabsItem>
            </TabsList>

            <TabsContent value="dashboard">
              <h2 className="py-6 text-xl font-medium text-metal-600">
                Dashboard
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200 w-full">
                  <h3 className="text-body-4 text-metal-600">Webisode</h3>
                  <Select
                    value={settings.dashboardWebisode}
                    onValueChange={value =>
                      setSettings(prev => ({
                        ...prev,
                        dashboardWebisode: value,
                      }))
                    }
                  >
                    <SelectAction className="whitespace-nowrap">
                      <SelectValue placeholder="Selecione webisode" />
                    </SelectAction>
                    <SelectContent>
                      {webisodes &&
                        webisodes.map(webisode => (
                          <SelectItem
                            key={webisode.id}
                            value={webisode.id!.toString()}
                          >
                            {webisode.title}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">Contactos</h3>
                  <Select
                    value={settings.dashboardContacts}
                    onValueChange={value =>
                      setSettings(prev => ({
                        ...prev,
                        dashboardContacts: value,
                      }))
                    }
                  >
                    <SelectAction>
                      <SelectValue placeholder="Selecione contactos" />
                    </SelectAction>
                    <SelectContent>
                      {pages &&
                        pages.map(page => (
                          <SelectItem key={page.id} value={page.id!.toString()}>
                            {page.title}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">Formação</h3>
                  <Select
                    value={settings.dashboardTraining}
                    onValueChange={value =>
                      setSettings(prev => ({
                        ...prev,
                        dashboardTraining: value,
                      }))
                    }
                  >
                    <SelectAction>
                      <SelectValue placeholder="Selecione formação" />
                    </SelectAction>
                    <SelectContent>
                      {trainings &&
                        trainings.map(training => (
                          <SelectItem
                            key={training.id}
                            value={training.id!.toString()}
                          >
                            {training.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="redes-sociais">
              <h2 className="py-6 text-xl font-medium text-metal-600">
                Redes Sociais
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">Facebook</h3>
                  <Input
                    type="text"
                    placeholder="Link de facebook"
                    value={settings.socialNetworkFacebook}
                    onChange={e =>
                      setSettings(prev => ({
                        ...prev,
                        socialNetworkFacebook: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">Instagram</h3>
                  <Input
                    type="text"
                    placeholder="Link de instagram"
                    value={settings.socialNetworkInstagram}
                    onChange={e =>
                      setSettings(prev => ({
                        ...prev,
                        socialNetworkInstagram: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">Linkedin</h3>
                  <Input
                    type="text"
                    placeholder="Link de Linkedin"
                    value={settings.socialNetworkLinkedin}
                    onChange={e =>
                      setSettings(prev => ({
                        ...prev,
                        socialNetworkLinkedin: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">Youtube</h3>
                  <Input
                    type="text"
                    placeholder="Link de YouTube"
                    value={settings.socialNetworkYouTube}
                    onChange={e =>
                      setSettings(prev => ({
                        ...prev,
                        socialNetworkYouTube: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="contatos">
              <h2 className="py-6 text-xl font-medium text-metal-600">
                Contatos
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">
                    Barra de contatos
                  </h3>
                  <Input
                    type="text"
                    placeholder="Insira o texto da barra de contatos"
                    value={settings.contact_bar_text}
                    onChange={e =>
                      setSettings(prev => ({
                        ...prev,
                        contact_bar_text: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">
                    Telefone de contato
                  </h3>
                  <Input
                    type="text"
                    placeholder="Insira o telefone para contato"
                    value={settings.phoneForContacts}
                    onChange={e =>
                      setSettings(prev => ({
                        ...prev,
                        phoneForContacts: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">
                    Email para contato
                  </h3>
                  <Input
                    type="text"
                    placeholder="Insira o email para contato"
                    value={settings.emailThatReceivesContacts}
                    onChange={e =>
                      setSettings(prev => ({
                        ...prev,
                        emailThatReceivesContacts: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">
                    Email para envio de simulações
                  </h3>
                  <Input
                    type="text"
                    placeholder="Insira o email para envio de simulações"
                    value={settings.emailThatReceivesSimulationRequests}
                    onChange={e =>
                      setSettings(prev => ({
                        ...prev,
                        emailThatReceivesSimulationRequests: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">
                    Email para recepção de participações de sinistros
                  </h3>
                  <Input
                    type="text"
                    placeholder="Insira o email para recepção de participações de sinistros"
                    value={settings.emailThatReceivesSinistersParticipations}
                    onChange={e =>
                      setSettings(prev => ({
                        ...prev,
                        emailThatReceivesSinistersParticipations:
                          e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="paginas">
              <h2 className="py-6 text-xl font-medium text-metal-600">
                Páginas
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">
                    Participação de Sinistros
                  </h3>
                  <Select
                    value={settings.pagesClaimsProcess}
                    onValueChange={value =>
                      setSettings(prev => ({
                        ...prev,
                        pagesClaimsProcess: value,
                      }))
                    }
                  >
                    <SelectAction>
                      <SelectValue placeholder="Selecione participação de sinistros" />
                    </SelectAction>
                    <SelectContent>
                      {pages &&
                        pages.map(page => (
                          <SelectItem
                            key={page.slug}
                            value={page.slug!.toString()}
                          >
                            {page.title}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="ficheiros">
              <h2 className="py-6 text-xl font-medium text-metal-600">
                Ficheiros
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">
                    Ficha de nomeação
                  </h3>
                  <>
                    {settingFiles?.find(s => s.identifier === 'ficha_nomeacao')
                      ?.file ? (
                      <p className="flex items-center gap-4">
                        <a
                          href={
                            settingFiles.find(
                              s => s.identifier === 'ficha_nomeacao',
                            )?.file as unknown as string
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-body-4 bg-metal-50 text-metal-900 hover:bg-metal-100 transition-colors"
                        >
                          Ver Ficheiro
                        </a>
                        <Button
                          color="secondary"
                          variant="softBg"
                          onClick={() => onDeleteFile('ficha_nomeacao')}
                        >
                          <TrashSimple />
                        </Button>
                      </p>
                    ) : (
                      <Upload
                        className="p-0"
                        horizontal
                        options={{
                          onDrop: (acceptedFile: File[]) =>
                            onDrop(acceptedFile[0], 'ficha_nomeacao'),
                          multiple: false,
                        }}
                      >
                        <UploadBody className="border-0">
                          <UploadText className="text-body-4 text-metal-300">
                            Apenas PDF
                          </UploadText>
                        </UploadBody>
                      </Upload>
                    )}
                  </>
                </div>

                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">
                    Autorização de Debito Directo
                  </h3>
                  <>
                    {settingFiles?.find(
                      s => s.identifier === 'autorizacao_debito_directo',
                    )?.file ? (
                      <p className="flex items-center gap-4">
                        <a
                          href={
                            settingFiles.find(
                              s =>
                                s.identifier === 'autorizacao_debito_directo',
                            )?.file as unknown as string
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-body-4 bg-metal-50 text-metal-900 hover:bg-metal-100 transition-colors"
                        >
                          Ver Ficheiro
                        </a>
                        <Button
                          color="secondary"
                          variant="softBg"
                          onClick={() =>
                            onDeleteFile('autorizacao_debito_directo')
                          }
                        >
                          <TrashSimple />
                        </Button>
                      </p>
                    ) : (
                      <Upload
                        className="p-0"
                        horizontal
                        options={{
                          onDrop: (acceptedFile: File[]) =>
                            onDrop(
                              acceptedFile[0],
                              'autorizacao_debito_directo',
                            ),
                          multiple: false,
                        }}
                      >
                        <UploadBody className="border-0">
                          <UploadText className="text-body-4 text-metal-300">
                            Apenas PDF
                          </UploadText>
                        </UploadBody>
                      </Upload>
                    )}
                  </>
                </div>

                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">
                    Formulário de Sinistros
                  </h3>
                  <>
                    {settingFiles?.find(
                      s => s.identifier === 'formulario_de_sinistros',
                    )?.file ? (
                      <p className="flex items-center gap-4">
                        <a
                          href={
                            settingFiles.find(
                              s => s.identifier === 'formulario_de_sinistros',
                            )?.file as unknown as string
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-body-4 bg-metal-50 text-metal-900 hover:bg-metal-100 transition-colors"
                        >
                          Ver Ficheiro
                        </a>
                        <Button
                          color="secondary"
                          variant="softBg"
                          onClick={() =>
                            onDeleteFile('formulario_de_sinistros')
                          }
                        >
                          <TrashSimple />
                        </Button>
                      </p>
                    ) : (
                      <Upload
                        className="p-0"
                        horizontal
                        options={{
                          onDrop: (acceptedFile: File[]) =>
                            onDrop(acceptedFile[0], 'formulario_de_sinistros'),
                          multiple: false,
                        }}
                      >
                        <UploadBody className="border-0">
                          <UploadText className="text-body-4 text-metal-300">
                            Apenas PDF
                          </UploadText>
                        </UploadBody>
                      </Upload>
                    )}
                  </>
                </div>

                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">
                    Comprovativo de IBAN Innovarisk
                  </h3>
                  <>
                    {settingFiles?.find(
                      s => s.identifier === 'comprovativo_de_iban_innovarisk',
                    )?.file ? (
                      <p className="flex items-center gap-4">
                        <a
                          href={
                            settingFiles.find(
                              s =>
                                s.identifier ===
                                'comprovativo_de_iban_innovarisk',
                            )?.file as unknown as string
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-body-4 bg-metal-50 text-metal-900 hover:bg-metal-100 transition-colors"
                        >
                          Ver Ficheiro
                        </a>
                        <Button
                          color="secondary"
                          variant="softBg"
                          onClick={() =>
                            onDeleteFile('comprovativo_de_iban_innovarisk')
                          }
                        >
                          <TrashSimple />
                        </Button>
                      </p>
                    ) : (
                      <Upload
                        className="p-0"
                        horizontal
                        options={{
                          onDrop: (acceptedFile: File[]) =>
                            onDrop(
                              acceptedFile[0],
                              'comprovativo_de_iban_innovarisk',
                            ),
                          multiple: false,
                        }}
                      >
                        <UploadBody className="border-0">
                          <UploadText className="text-body-4 text-metal-300">
                            Apenas PDF
                          </UploadText>
                        </UploadBody>
                      </Upload>
                    )}
                  </>
                </div>

                <div className="grid grid-cols-2 gap-4 items-center pb-4 mb-4 border-b border-metal-200">
                  <h3 className="text-body-4 text-metal-600">
                    Declaração de Ausência de Sinistro
                  </h3>
                  <>
                    {settingFiles?.find(
                      s =>
                        s.identifier === 'declaracao_de_ausencia_de_sinistro',
                    )?.file ? (
                      <p className="flex items-center gap-4">
                        <a
                          href={
                            settingFiles.find(
                              s =>
                                s.identifier ===
                                'declaracao_de_ausencia_de_sinistro',
                            )?.file as unknown as string
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-body-4 bg-metal-50 text-metal-900 hover:bg-metal-100 transition-colors"
                        >
                          Ver Ficheiro
                        </a>
                        <Button
                          color="secondary"
                          variant="softBg"
                          onClick={() =>
                            onDeleteFile('declaracao_de_ausencia_de_sinistro')
                          }
                        >
                          <TrashSimple />
                        </Button>
                      </p>
                    ) : (
                      <Upload
                        className="p-0"
                        horizontal
                        options={{
                          onDrop: (acceptedFile: File[]) =>
                            onDrop(
                              acceptedFile[0],
                              'declaracao_de_ausencia_de_sinistro',
                            ),
                          multiple: false,
                        }}
                      >
                        <UploadBody className="border-0">
                          <UploadText className="text-body-4 text-metal-300">
                            Apenas PDF
                          </UploadText>
                        </UploadBody>
                      </Upload>
                    )}
                  </>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </Wrapper>
    </div>
  )
}
